











































































































import Vue from 'vue';
import store from '@/store';
import { MemberRole } from '@/models';
import { OrgPeerGroup } from '@/models/orgStructure';
import { groupRolePlural } from '@/groupRole';
import {
  CopiedSettings,
  DuplicateProgress,
  DuplicateResult,
  DuplicateStep,
  copyIsIncomplete,
  duplicateName
} from '@/peerGroupDuplicate';

import DialogCard from '@/components/DialogCard.vue';
import PeerGroupListCreator from '@/components/PeerGroupListCreator.vue';
import { normalizeAgainstOrg } from '@/components/PeerGroupSettingsRoleEditor.vue';

type Phase = 'form' | 'working' | 'done';

interface Chosen {
  settings: boolean;
  cogs: boolean;
  teachers: boolean;
  students: boolean;
  observers: boolean;
}

interface Option {
  key: keyof Chosen;
  label: string;
  count: number;
}

const STEP_ORDER: DuplicateStep[] = [
  'create',
  'settings',
  'cogs',
  'members',
  'archive'
];

// Each role's checkbox, in the order they're listed.
const ROLE_PARTS: [keyof Chosen, MemberRole][] = [
  ['teachers', MemberRole.TEACHER],
  ['students', MemberRole.STUDENT],
  ['observers', MemberRole.OBSERVER]
];

const capitalized = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export default Vue.extend({
  components: {
    DialogCard,
    PeerGroupListCreator
  },

  props: {
    value: Boolean,

    peerGroupId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      phase: 'form' as Phase,
      // Remounts the form on each open, so its fields start over.
      openCount: 0,
      chosen: {
        settings: true,
        cogs: true,
        teachers: true,
        students: true,
        observers: true
      },
      archiveSource: false,
      archiveRequested: false,
      createFailed: false,
      progress: {} as { [step in DuplicateStep]?: DuplicateProgress },
      result: null as DuplicateResult | null,
      copyName: '',
      archiving: false
    };
  },

  computed: {
    // Opened and closed by the parent through v-model.
    isOpen: {
      get(): boolean {
        return this.value;
      },
      set(open: boolean) {
        this.$emit('input', open);
      }
    },

    peerGroup(): OrgPeerGroup {
      return store.getters.org.peerGroup(this.peerGroupId);
    },

    title(): string {
      return `Duplicate ${this.peerGroup.name}`;
    },

    initialName(): string {
      return duplicateName(this.peerGroup.name);
    },

    roleCounts(): { [role in MemberRole]: number } {
      const count = (role: MemberRole) =>
        this.peerGroup.members.filter(m => m.role === role).length;

      return {
        [MemberRole.TEACHER]: count(MemberRole.TEACHER),
        [MemberRole.STUDENT]: count(MemberRole.STUDENT),
        [MemberRole.OBSERVER]: count(MemberRole.OBSERVER)
      };
    },

    options(): Option[] {
      const cogs = this.peerGroup.packets.length;
      const roleOptions = ROLE_PARTS.map(([key, role]) => {
        const count = this.roleCounts[role];
        const plural = capitalized(groupRolePlural(role, this.peerGroup));

        return { key, label: `${plural} (${count})`, count };
      });

      return [
        { key: 'settings', label: 'Settings and role names', count: 1 },
        {
          key: 'cogs',
          label: `COGS (${cogs} ${cogs === 1 ? 'set' : 'sets'})`,
          count: cogs
        },
        ...roleOptions
      ];
    },

    // The same people who can archive it from Settings: admins and the
    // group's teachers.
    canArchive(): boolean {
      if (!this.peerGroup.isActive) {
        return false;
      }

      if (store.state.org.adminAccess) {
        return true;
      }

      const userId = store.state.org.userInfo.userId;

      return this.peerGroup.members.some(
        m => m.userId === userId && m.role === MemberRole.TEACHER
      );
    },

    archiveLabel(): string {
      return `Archive "${this.peerGroup.name}" after duplicating`;
    },

    steps(): (DuplicateProgress & { label: string; finished: boolean })[] {
      // Only steps that have started; skipped ones never report.
      return STEP_ORDER.map(step => this.progress[step])
        .filter((p): p is DuplicateProgress => !!p)
        .map(progress => ({
          ...progress,
          label: this.stepLabel(progress),
          finished: progress.done >= progress.total
        }));
    },

    missing(): string[] {
      if (!this.result) {
        return [];
      }

      return [
        ...(this.result.settingsFailed ? ['Settings and role names'] : []),
        ...this.result.failedPackets.map(p => `COGS: ${p.name}`),
        ...this.result.failedMembers.map(m =>
          m.name ? `${m.name} (${m.email})` : m.email
        )
      ];
    },

    // Archiving was asked for and hasn't happened, whether skipped or failed.
    showArchiveButton(): boolean {
      return this.archiveRequested && this.peerGroup.isActive;
    },

    archiveLine(): string {
      if (!this.archiveRequested) {
        return '';
      }

      return this.peerGroup.isActive
        ? `"${this.peerGroup.name}" was left active.`
        : `"${this.peerGroup.name}" was archived.`;
    }
  },

  methods: {
    stepLabel({ step, total }: DuplicateProgress): string {
      switch (step) {
        case 'create':
          return 'Creating group';
        case 'settings':
          return 'Copying settings';
        case 'cogs':
          return `Connecting ${total} COGS`;
        case 'members':
          return `Adding ${total} ${total === 1 ? 'member' : 'members'}`;
        case 'archive':
          return `Archiving "${this.peerGroup.name}"`;
      }
    },

    reset() {
      this.openCount += 1;
      this.phase = 'form';
      this.chosen = {
        settings: true,
        cogs: this.peerGroup.packets.length > 0,
        teachers: this.roleCounts[MemberRole.TEACHER] > 0,
        students: this.roleCounts[MemberRole.STUDENT] > 0,
        observers: this.roleCounts[MemberRole.OBSERVER] > 0
      };
      this.archiveSource = false;
      this.archiveRequested = false;
      this.createFailed = false;
      this.progress = {};
      this.result = null;
    },

    copiedSettings(): CopiedSettings {
      const pg = this.peerGroup;

      return {
        creatorCanPublish: pg.creatorCanPublish,
        teachersCanPublish: pg.teachersCanPublish,
        submissionStatusIsInteractive: pg.submissionStatusIsInteractive,
        ...normalizeAgainstOrg(pg, store.state.org.basic)
      };
    },

    async duplicate(form: {
      name: string;
      section: string;
      description: string;
    }) {
      this.copyName = form.name;
      this.createFailed = false;
      this.progress = {};
      this.archiveRequested = this.canArchive && this.archiveSource;
      this.phase = 'working';

      let result: DuplicateResult;

      try {
        result = await store.dispatch.org.duplicatePeerGroup({
          sourcePeerGroupId: this.peerGroupId,
          ...form,
          settings: this.chosen.settings ? this.copiedSettings() : null,
          parts: {
            cogs: this.chosen.cogs,
            teachers: this.chosen.teachers,
            students: this.chosen.students,
            observers: this.chosen.observers
          },
          archiveSource: this.archiveRequested,
          onProgress: progress => {
            this.progress = { ...this.progress, [progress.step]: progress };
          }
        });
      } catch (error) {
        console.error(error);
        this.createFailed = true;
        this.phase = 'form';
        return;
      }

      this.result = result;

      if (copyIsIncomplete(result) || result.archiveFailed) {
        this.phase = 'done';
      } else {
        this.openCopy();
      }
    },

    async archiveOriginal() {
      this.archiving = true;
      // Reports its own failure; the original just stays active.
      await store.dispatch.org.deactivatePeerGroup(this.peerGroupId);
      this.archiving = false;
    },

    openCopy() {
      if (!this.result) {
        return;
      }

      const organizationId = this.$route.params['organizationId'];
      const peerGroupId = `${this.result.peerGroupId}`;

      this.close();
      this.$router.push({
        name: 'OrganizationPeerGroup',
        params: { organizationId, peerGroupId }
      });
    },

    close() {
      this.isOpen = false;
    }
  },

  watch: {
    value(open: boolean) {
      if (open) {
        this.reset();
      }
    }
  }
});
